const API_URL = process.env.REACT_APP_API_URL ?? ''

export type AdminRole = 'ROLE_ADMIN' | 'ROLE_ORGANIZATION' | 'ROLE_DOCTOR'

function getLoginTokenFromCookie(): string | null {
  const match = document.cookie.match(/(?:^|; )loginToken=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

function authHeaders(): Record<string, string> {
  const token = getLoginTokenFromCookie()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

export async function requestVerificationCode(email: string, role: AdminRole): Promise<void> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/authentication/login/email/request-verification-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to send verification code.')
}

export async function verifyCode(email: string, role: AdminRole, verificationCode: string): Promise<string> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/authentication/login/email/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, role, verificationCode, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Verification failed.')
  return json.accessToken as string
}

export async function logout(): Promise<void> {
  await fetch(`${API_URL}/admin-portal/api/v1/authentication/logout`, {
    method: 'POST',
  })
}

export function decodeAccessToken(accessToken: string): { email?: string; role?: string } | null {
  try {
    const payload = accessToken.split('.')[1]
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return { email: decoded.sub, role: decoded.role }
  } catch {
    return null
  }
}

export interface OrganizationSummary {
  id: string
  username: string
  email: string
  adminUuid: string
  creationTimestamptz: string
}

export async function getOrganizations(): Promise<OrganizationSummary[]> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/organization/get/all`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ sorting: {}, paging: {}, filtering: {}, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to fetch organizations.')
  return json.success as OrganizationSummary[]
}

export async function deleteOrganization(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/organization/delete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ id, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to delete organization.')
}

export async function getOrganizationUuidByEmail(email: string): Promise<string | null> {
  const organizations = await getOrganizations()
  const match = organizations.find((org) => org.email.toLowerCase() === email.toLowerCase())
  return match?.id ?? null
}

export interface DoctorSummary {
  id: string
  username: string
  email: string
  organizationUuid: string
  creationTimestamptz: string
}

export async function getDoctors(): Promise<DoctorSummary[]> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/doctor/get/all`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ sorting: {}, paging: {}, filtering: {}, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to fetch doctors.')
  return json.success as DoctorSummary[]
}

export async function deleteDoctor(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/doctor/delete`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ id, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to delete doctor.')
}

export async function getDoctorUuidByEmail(email: string): Promise<string | null> {
  const doctors = await getDoctors()
  const match = doctors.find((doctor) => doctor.email.toLowerCase() === email.toLowerCase())
  return match?.id ?? null
}

const ROLE_PATH_PREFIX: Record<AdminRole, string> = {
  ROLE_ADMIN: 'admin-user',
  ROLE_ORGANIZATION: 'organization',
  ROLE_DOCTOR: 'doctor',
}

export interface OwnProfile {
  id: string
  username: string
  email: string
  profilePhotoUrl?: string
}

export async function getOwnUuidByEmail(email: string, role: AdminRole): Promise<string | null> {
  switch (role) {
    case 'ROLE_ADMIN':
      return getAdminUuidByEmail(email)
    case 'ROLE_ORGANIZATION':
      return getOrganizationUuidByEmail(email)
    case 'ROLE_DOCTOR':
      return getDoctorUuidByEmail(email)
  }
}

export async function getProfile(id: string, role: AdminRole): Promise<OwnProfile> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/${ROLE_PATH_PREFIX[role]}/get`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ id, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to fetch profile.')
  return json.success as OwnProfile
}

export async function updateUsername(id: string, role: AdminRole, username: string): Promise<void> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/${ROLE_PATH_PREFIX[role]}/update/username`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ id, username, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to update username.')
}

export async function updateProfilePhotoUrl(id: string, role: AdminRole, profilePhotoUrl: string): Promise<void> {
  if (role === 'ROLE_ORGANIZATION') {
    throw new Error('Avatar updates are not supported for organization accounts.')
  }
  const res = await fetch(`${API_URL}/admin-portal/api/v1/${ROLE_PATH_PREFIX[role]}/update/profile-photo-url`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ id, profilePhotoUrl, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to update profile photo.')
}

export async function getAdminUuidByEmail(email: string): Promise<string | null> {
  const res = await fetch(`${API_URL}/admin-portal/api/v1/admin-user/get/all`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ sorting: {}, paging: {}, filtering: {}, otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Failed to look up admin user.')
  const match = (json.success as { id: string; email: string }[]).find(
    (admin) => admin.email.toLowerCase() === email.toLowerCase()
  )
  return match?.id ?? null
}

export interface SignUpValues {
  username: string
  email: string
  role: AdminRole
  adminUuid?: string
  organizationUuid?: string
}

const SIGN_UP_PATH: Record<AdminRole, string> = {
  ROLE_ADMIN: 'sign-up/admin-user',
  ROLE_ORGANIZATION: 'sign-up/organization',
  ROLE_DOCTOR: 'sign-up/doctor',
}
function generateUnusedPassword(): string {
  const symbols = '!@#$%^&*'
  const randomSegment = () => Math.random().toString(36).slice(-8)
  const symbol = symbols[Math.floor(Math.random() * symbols.length)]
  return `Aa1${symbol}${randomSegment()}`
}

export async function signUp(values: SignUpValues): Promise<{ accessToken: string; refreshToken?: string }> {
  const path = SIGN_UP_PATH[values.role]
  const res = await fetch(`${API_URL}/admin-portal/api/v1/authentication/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...values, password: generateUnusedPassword(), otherInformation: {} }),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.message ?? 'Sign up failed.')
  return { accessToken: json.accessToken, refreshToken: json.refreshToken }
}
